import React from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import 'react-quill/dist/quill.snow.css';

// Context & Global Components
import { Toaster } from 'react-hot-toast';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './context/AuthProvider';

// Layouts
import PublicLayout from './components/layouts/PublicLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Public pages (for guests & customers)
import HomePage from './pages/HomePage';
import ProductDetail from './components/ProductDetail';
import Shop from './components/Shop';
import CartPage from './components/CartPage';
import CheckoutPage from './components/CheckoutPage';
import Wishlist from './components/Wishlist';
import AuthPage from './pages/AuthPage';
import ThankYouPage from './components/ThankYouPage';
import GuaranteeSearch from './components/GuaranteeSearch';
import GuaranteePage from './components/GuaranteePage';
import DirectGuaranteePage from './components/DirectGuaranteePage';
import DirectGuaranteeSummary from './components/DirectGuaranteeSummary';
import Invoice from './components/Invoice';
import OnlineOrderInvoice from './components/OnlineOrderInvoice';
import GuaranteeSummary from './components/GuaranteeSummary';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import FaqPage from './components/FaqPage';
import PoliciesPage from './components/PoliciesPage';
import MyOrdersPage from './components/MyOrdersPage';
import AccountPage from "./pages/AccountPage";
import EditProfilePage from "./pages/EditProfilePage";
import MyAddressesPage from "./pages/MyAddressesPage";
import UnauthorizedPage from './pages/UnauthorizedPage';
import LiveGoldRatePage from "./components/LiveGoldRatePage";

// Admin pages (for admin & staff)
import AdminProductList from './components/AdminProductList';
import CustomerServiceDashboard from './components/CustomerServiceDashboard';
import FactoryDashboard from './components/FactoryDashboard';
import AdminSettings from './components/AdminSettings';
import AddProduct from './components/AddProduct';
import OrderForm from './components/OrderForm';
import NotifyRequests from './components/admin/NotifyRequests';
import AdminUsersPage from "./pages/AdminUsersPage";
import PrintOrders from './components/PrintOrders';

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-center" />
      <ToastContainer position="top-right" autoClose={3000} />

      <Routes>
        {/* Public-facing routes */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
        <Route path="/product/:productId" element={<PublicLayout><ProductDetail /></PublicLayout>} />
        <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
        <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
        <Route path="/wishlist" element={<PublicLayout><Wishlist /></PublicLayout>} />
        <Route path="/auth" element={<PublicLayout><AuthPage /></PublicLayout>} />
        <Route path="/thank-you" element={<PublicLayout><ThankYouPage /></PublicLayout>} />
        <Route path="/guarantee/:orderId" element={<PublicLayout><GuaranteePage /></PublicLayout>} />
        <Route path="/guarantee-summary/:orderId" element={<PublicLayout><GuaranteeSummary /></PublicLayout>} />
        <Route path="/guarantee/direct/:orderId/:itemId" element={<PublicLayout><DirectGuaranteePage /></PublicLayout>} />
        <Route path="/guarantee/direct/:orderId" element={<PublicLayout><DirectGuaranteeSummary /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
        <Route path="/faq" element={<PublicLayout><FaqPage /></PublicLayout>} />
        <Route path="/policies" element={<PublicLayout><PoliciesPage /></PublicLayout>} />
        <Route path="/my-orders" element={<PublicLayout><MyOrdersPage /></PublicLayout>} />
        <Route path="/account" element={<PublicLayout><AccountPage /></PublicLayout>} />
        <Route path="/account/edit" element={<PublicLayout><EditProfilePage /></PublicLayout>} />
        <Route path="/account/addresses" element={<PublicLayout><MyAddressesPage /></PublicLayout>} />
        <Route path="/unauthorized" element={<PublicLayout><UnauthorizedPage /></PublicLayout>} />
        <Route path="/live-gold" element={<PublicLayout><LiveGoldRatePage /></PublicLayout>} />

        {/* Admin/staff-facing routes */}
        <Route path="/admin/products" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminLayout><AdminProductList /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/add-product" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminLayout><AddProduct /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminLayout><AdminUsersPage /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/factory" element={
          <ProtectedRoute allowedRoles={['factory', 'admin']}>
            <AdminLayout><FactoryDashboard /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/order-form" element={
          <ProtectedRoute allowedRoles={['factory', 'customer-service', 'admin']}>
            <AdminLayout><OrderForm /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/customer-service-dashboard" element={
          <ProtectedRoute allowedRoles={['customer-service', 'admin']}>
            <AdminLayout><CustomerServiceDashboard /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin-settings" element={
          <ProtectedRoute allowedRoles={['customer-service', 'admin']}>
            <AdminLayout><AdminSettings /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/admin/notify-requests" element={
          <ProtectedRoute allowedRoles={['customer-service', 'admin']}>
            <AdminLayout><NotifyRequests /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/guarantee-search" element={
          <ProtectedRoute allowedRoles={['admin', 'factory', 'customer-service']}>
            <AdminLayout><GuaranteeSearch /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/invoice/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'factory', 'customer-service']}>
            <AdminLayout><Invoice /></AdminLayout>
          </ProtectedRoute>
        } />

        <Route path="/invoice/direct/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'factory', 'customer-service']}>
            <AdminLayout><OnlineOrderInvoice /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/print-orders" element={<PrintOrders />} />


      </Routes>
    </AuthProvider>
  );
}

export default App;
